import { useCallback, useEffect, useState, useRef } from "react"
import { toast } from "react-toastify"
import {
  DiscountData,
  getDiscountByEnrollment,
} from "../../../service/discount"
import { Enrollment } from "../../../service/enrollment"
import {
  createMonthlyFee,
  getFeeReceiptUrl,
  InvoiceItem,
  MonthlyFeeRequest,
  ReceiptPrinter,
} from "../../../service/fee-invoices"
import { getAllFeeType } from "../../../service/fees"
import { TUITION_FEE_GROUP } from "../../../constant"
import InvoicePrintModal from "../../peoples/students/student-details/invoicePrintModal"
import { useNavigate } from "react-router-dom"
import { useSelector } from "react-redux"
import { selectUser } from "../../../core/data/redux/slice/user"

type Props = {
  studentData?: Enrollment | null
  selectedSystem?: string
  feeGroup?: string
  onClose: () => void
}

type MonthlyFeeItem = {
  id: string
  month: string
  // Each FeeType in the Tuition group (Tuition Fee, STEAM Education Fee,
  // etc.) gets its OWN independent row, same as Tuition Fee always has —
  // not merged into one line. A student who shouldn't be billed STEAM just
  // has that row deleted (same trash action every row already has).
  feeTypeId: string
  feeTypeName: string
  fees: number
  discount: {
    profile: number
    manual: number
  }
  fine: number
  amount: number
  note: string
}

type OtherFeeItem = {
  id: string
  name: string
  amount: number
  discount: {
    profile: number
    manual: number
  }
  note: string
  feeGroupId?: string
  feeGroupName?: string
  feeTypeId?: string
  metaData?: { module?: string; type?: string; issuedBookLogId?: string }
}

const CollectFeesModal = ({
  selectedSystem,
  studentData,
  feeGroup,
  onClose,
}: Props) => {
  const user = useSelector(selectUser)
  const [loading, setLoading] = useState(true)
  const [createFeeLoading, setCreateFeeLoading] = useState(false)
  const [monthlyFeesData, setMonthlyFeesData] = useState<MonthlyFeeItem[]>([])
  const [otherFeesData, setOtherFeesData] = useState<OtherFeeItem[]>([])
  const [remainingMonths, setRemainingMonths] = useState<string[]>([])
  const [allAvailableMonths, setAllAvailableMonths] = useState<string[]>([])
  // Every active FeeType under the "Tuition" group for this class (Tuition
  // Fee, STEAM Education Fee, ...) — each becomes its own row when a month
  // is added; staff delete whichever row a given student doesn't need.
  const [tuitionFeeTypes, setTuitionFeeTypes] = useState<any[]>([])
  // monthKey ("YYYY-MM") -> FeeType _ids already invoiced for that month —
  // so re-clicking a partially-paid month only re-adds what's still missing
  // instead of re-adding (and risking double-billing) what's already paid.
  const [paidFeeTypesByMonth, setPaidFeeTypesByMonth] = useState<
    Record<string, string[]>
  >({})
  const [discountData, setDiscountData] = useState<DiscountData>()
  const [otherFees, setOtherFees] = useState<any[]>([])
  const [generatedInvoice, setGeneratedInvoice] = useState<any>(null)
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [feeErrorMessage, setFeeErrorMessage] = useState<string | null>(null)
  const [withPrint, setWithPrint] = useState<boolean | null>(false)

  const printRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // Backend renders two different receipts from the same invoice id — an A4
  // PDF ("normal") or a 76mm auto-printing HTML page calibrated for the
  // Epson TM-U220 ("thermal"); see mpa-api's /api/receipt route. Opening it
  // in a new tab is the closest a browser lets us get to "print to a chosen
  // printer" — it still can't pick the OS print-dialog's printer for the
  // user, only get the right paper size/layout in front of them.
  const handlePrint = (printer: ReceiptPrinter) => {
    if (!generatedInvoice?._id) return
    window.open(getFeeReceiptUrl(generatedInvoice._id, printer), "_blank")
    setShowPrintModal(false)
    setGeneratedInvoice(null)
  }

  const handleViewInvoices = () => {
    handleCloseModal()
    if (studentData?._id) {
      navigate(`/student/student-details/${studentData._id}/fees`)
    }
  }

  const buildFeeRow = (
    month: string,
    ft: any,
    computeDiscount: (baseFee: number) => number,
  ): MonthlyFeeItem => {
    // Fine is a per-MONTH late charge, not per-fee-type — a month gets its
    // own row per active FeeType in the group (e.g. Tuition Fee + STEAM
    // Education Fee both under "Tuition"), so applying the fine to every
    // row would charge it once per fee type instead of once per month.
    // Apply it only to the Tuition Fee row; same name check the group's
    // own sort order already uses to identify it.
    const isTuitionFeeType = /tuition/i.test(ft?.name || "")
    const fineAmount = isTuitionFeeType ? calculateFine(month) : 0
    // Profile discount only ever reduces Tuition Fee — never STEAM or any
    // other fee type in the group, even when Tuition isn't due this month
    // (e.g. already invoiced separately). Capped at what the Tuition row
    // can actually use (fee + fine): computeDiscount returns the student's
    // raw configured discount (e.g. a flat 20000 PKR amount-type discount)
    // with no relation to the fee size, so without the cap a small Tuition
    // fee could show a discount far larger than itself.
    const profileDiscountAmount = isTuitionFeeType
      ? Math.min(computeDiscount(ft?.amount || 0), (ft?.amount || 0) + fineAmount)
      : 0
    return {
      id: Math.random().toString(36).substr(2, 9),
      month,
      feeTypeId: ft?._id,
      feeTypeName: ft?.name || "Fee",
      fees: ft?.amount || 0,
      discount: { profile: profileDiscountAmount, manual: 0 },
      fine: fineAmount,
      amount: Math.max(0, (ft?.amount || 0) - profileDiscountAmount + fineAmount),
      note: fineAmount > 0 ? `Fine: ${fineAmount}` : "",
    }
  }

  // profileDiscountAmount is the discount ALREADY attributed to this
  // specific row (0 for every fee type except the month's first due one —
  // see buildFeeRow) — never recompute it fresh here, that reintroduces the
  // "every fee type gets the full discount" bug.
  const calculateMaxAllowedDiscount = (
    baseFee: number,
    fine: number = 0,
    profileDiscountAmount: number = 0,
  ): number => Math.max(0, baseFee + fine - profileDiscountAmount)

  const calculateProfileDiscount = (baseFee: number): number => {
    if (!discountData) {
      return 0
    }

    const profileDiscountAmount =
      discountData.discountType === "percentage"
        ? baseFee * (discountData.discount / 100)
        : discountData.discount

    return profileDiscountAmount
  }

  const formatMonthToShort = (monthString: string): string => {
    if (monthString.match(/^\d{4}-\d{2}$/)) {
      const [year, month] = monthString.split("-")
      const monthMap: Record<string, string> = {
        "01": "Jan",
        "02": "Feb",
        "03": "Mar",
        "04": "Apr",
        "05": "May",
        "06": "Jun",
        "07": "Jul",
        "08": "Aug",
        "09": "Sep",
        "10": "Oct",
        "11": "Nov",
        "12": "Dec",
      }
      return `${monthMap[month]}(${year})`
    }

    const match = monthString.match(/([A-Za-z]+)\((\d{4})\)/)
    if (!match) return monthString

    const monthName = match[1]
    const year = match[2]

    const monthMap: Record<string, string> = {
      January: "Jan",
      February: "Feb",
      March: "Mar",
      April: "Apr",
      May: "May",
      June: "Jun",
      July: "Jul",
      August: "Aug",
      September: "Sep",
      October: "Oct",
      November: "Nov",
      December: "Dec",
    }

    const shortMonth = monthMap[monthName] || monthName.substring(0, 3)
    return `${shortMonth}(${year})`
  }

  const formatMonthToBackend = (monthString: string): string => {
    const match = monthString.match(/([A-Za-z]+)\((\d{4})\)/)
    if (!match) return monthString

    const shortMonth = match[1]
    const year = match[2]

    const monthMap: Record<string, string> = {
      Jan: "01",
      Feb: "02",
      Mar: "03",
      Apr: "04",
      May: "05",
      Jun: "06",
      Jul: "07",
      Aug: "08",
      Sep: "09",
      Oct: "10",
      Nov: "11",
      Dec: "12",
    }

    const monthNumber = monthMap[shortMonth] || "01"
    return `${year}-${monthNumber}`
  }

  const monthMap: Record<string, number> = {
    Jan: 0,
    Feb: 1,
    Mar: 2,
    Apr: 3,
    May: 4,
    Jun: 5,
    Jul: 6,
    Aug: 7,
    Sep: 8,
    Oct: 9,
    Nov: 10,
    Dec: 11,
  }

  const calculateFine = (monthString: string): number => {
    const match = monthString.match(/([A-Za-z]+)\((\d{4})\)/)
    if (!match) return 0

    const monthName = match[1] as keyof typeof monthMap
    const year = parseInt(match[2], 10)
    const month = monthMap[monthName]
    const today = new Date()

    if (
      year > today.getFullYear() ||
      (year === today.getFullYear() && month > today.getMonth())
    ) {
      return 0
    }

    // Matches the backend's schedule exactly (services/defaulter-listing.js
    // getFineForMonth) -- this modal used to compute its own separate,
    // uncapped-by-month-length formula (floor((daysPassed-1)/10)*100, which
    // landed on 200/300 depending on whether the month had 30 or 31 days),
    // so the same student's fine could show differently here than on the
    // Defaulters List/fee card for the exact same unpaid month. A fully
    // elapsed month is now always a flat 400, same as there.
    const fullyElapsed =
      year < today.getFullYear() ||
      (year === today.getFullYear() && month < today.getMonth())
    if (fullyElapsed) return 400

    // Still the current month -- day-of-month ladder, same thresholds as
    // the backend (due after the 10th: 11-20 -> 100, 21-30 -> 200, 31+ -> 400).
    const dayOfMonth = today.getDate()
    if (dayOfMonth <= 10) return 0
    if (dayOfMonth <= 20) return 100
    if (dayOfMonth <= 30) return 200
    return 400
  }

  /** True once the 1st of this month has passed — independent of the fine
   * threshold, which only kicks in on day 11. This is what decides whether
   * a month's row should already be sitting in the collection table. */
  const isMonthStarted = (monthString: string): boolean => {
    const match = monthString.match(/([A-Za-z]+)\((\d{4})\)/)
    if (!match) return false

    const monthName = match[1] as keyof typeof monthMap
    const year = parseInt(match[2], 10)
    const month = monthMap[monthName]
    const today = new Date()

    if (year > today.getFullYear()) return false
    if (year === today.getFullYear() && month > today.getMonth()) return false
    return true
  }

  const handleMonthSelect = (month: string) => {
    if (monthlyFeesData.some(item => item.month === month)) {
      toast.warning(`Month ${month} is already added`)
      return
    }

    // One row per active FeeType in the group that ISN'T already invoiced
    // for this month — e.g. if Tuition was already paid for Aug but STEAM
    // wasn't, clicking Aug again only re-adds the STEAM row, never a second
    // Tuition row for a month already billed.
    const alreadyPaidIds = paidFeeTypesByMonth[formatMonthToBackend(month)] || []
    const dueFeeTypes = tuitionFeeTypes.filter(
      ft => !alreadyPaidIds.includes(ft._id),
    )
    const newRows = dueFeeTypes.map(ft =>
      buildFeeRow(month, ft, calculateProfileDiscount),
    )

    setMonthlyFeesData(prev => [...prev, ...newRows])
    setRemainingMonths(prev => prev.filter(m => m !== month))
  }

  const handleOtherFeeSelect = (feeItem: OtherFeeItem) => {
    if (otherFeesData.some(item => item.id === feeItem.id)) {
      toast.warning(`Fee ${feeItem.name} is already added`)
      return
    }

    const newOtherFeeItem: OtherFeeItem = {
      ...feeItem,
      discount: {
        profile: 0,
        manual: 0,
      },
    }

    setOtherFeesData(prev => [...prev, newOtherFeeItem])
  }

  const handleFeeChange = (index: number, field: string, value: string) => {
    const updatedFees = [...monthlyFeesData]
    const numValue = Number(value) || 0

    // Reuse the profile discount already stored on this row (correctly
    // zero for every fee type except the month's first due one — see
    // buildFeeRow) rather than recomputing it from scratch, which would
    // reintroduce the "every fee type gets the full discount" bug for
    // whichever row the operator happens to edit here.
    const profileDiscountAmount = updatedFees[index].discount.profile

    if (field === "discount.manual") {
      const maxAllowedDiscount = calculateMaxAllowedDiscount(
        updatedFees[index].fees,
        updatedFees[index].fine,
        profileDiscountAmount,
      )

      if (numValue > maxAllowedDiscount) {
        return
      }

      updatedFees[index] = {
        ...updatedFees[index],
        discount: {
          ...updatedFees[index].discount,
          manual: numValue,
        },
      }
    } else {
      updatedFees[index] = {
        ...updatedFees[index],
        [field]: field === "note" ? value : numValue,
      }
    }

    updatedFees[index].amount = Math.max(
      0,
      updatedFees[index].fees -
        updatedFees[index].discount.manual -
        profileDiscountAmount +
        updatedFees[index].fine,
    )

    setMonthlyFeesData(updatedFees)
  }


  const handleOtherFeeChange = (
    index: number,
    field: string,
    value: string,
  ) => {
    const updatedFees = [...otherFeesData]
    const numValue = Number(value) || 0

    if (field === "discount.manual") {
      if (numValue > updatedFees[index].amount) {
        return
      }
      updatedFees[index] = {
        ...updatedFees[index],
        discount: {
          ...updatedFees[index].discount,
          manual: numValue,
        },
      }
    } else if (field === "amount") {
      updatedFees[index] = {
        ...updatedFees[index],
        amount: numValue,
      }
    } else {
      updatedFees[index] = {
        ...updatedFees[index],
        [field]: field === "note" ? value : numValue,
      }
    }

    setOtherFeesData(updatedFees)
  }

  const removeFeeRow = (index: number) => {
    const removedFeeItem = monthlyFeesData[index]
    const removedMonth = removedFeeItem.month

    setMonthlyFeesData(prev => prev.filter((_, i) => i !== index))
    setRemainingMonths(prev => {
      const updatedMonths = [...prev, removedMonth]

      return updatedMonths.sort((a, b) => {
        const getMonthValue = (monthStr: string) => {
          const match = monthStr.match(/([A-Za-z]+)\((\d{4})\)/)
          if (!match) return 0
          const monthName = match[1] as keyof typeof monthMap
          const year = parseInt(match[2], 10)
          return year * 12 + monthMap[monthName]
        }
        return getMonthValue(a) - getMonthValue(b)
      })
    })
  }

  const removeOtherFeeRow = (index: number) => {
    setOtherFeesData(prev => prev.filter((_, i) => i !== index))
  }

  const calculateGrandTotal = () => {
    const monthlySubtotal = monthlyFeesData.reduce((sum, item) => {
      // item.discount.profile is already correct per row (0 for every fee
      // type except the month's first due one) — don't recompute it fresh.
      const profileDiscountAmount = item.discount.profile
      return (
        sum +
        Math.max(0, item.fees - item.discount.manual - profileDiscountAmount + item.fine)
      )
    }, 0)

    const otherFeesSubtotal = otherFeesData.reduce((sum, item) => {
      return sum + (item.amount - item.discount.manual)
    }, 0)

    return monthlySubtotal + otherFeesSubtotal
  }

  const calculateFinalAmount = (record: any) => {
    const subTotal = record.subTotal || 0
    const fine = record.fine || 0
    const totalDiscount =
      (record.discount?.profile || 0) + (record.discount?.manual || 0)
    return subTotal - totalDiscount + fine
  }

  const handleGenerateInvoice = async (isPrint?: boolean) => {
    if (monthlyFeesData.length === 0 && otherFeesData.length === 0) {
      toast.error("Please add months or other fees to generate invoice")
      return
    }

    if (!studentData?._id) {
      toast.error("Enrollment information not found")
      return
    }

    const isLibraryFineItem = (item: OtherFeeItem) =>
      (item.metaData?.module === "library" &&
        (item.metaData?.type === "damage" ||
          item.metaData?.type === "overdue" ||
          item.metaData?.type === "misplaced")) ||
      /library\s+damage\s+fine/i.test(item.name.trim()) ||
      /library\s+overdue\s+fine/i.test(item.name.trim()) ||
      /library\s+misplaced\s+fine/i.test(item.name.trim())

    const selectedLibraryFines = otherFeesData
      .filter(
        item =>
          isLibraryFineItem(item) &&
          item.metaData?.issuedBookLogId &&
          (item.metaData?.type === "damage" ||
            item.metaData?.type === "overdue" ||
            item.metaData?.type === "misplaced"),
      )
      .map(item => ({
        issuedBookLogId: item.metaData!.issuedBookLogId!,
        type: item.metaData!.type! as "damage" | "overdue" | "misplaced",
        amount: item.amount,
        manualDiscount: item.discount?.manual ?? 0,
      }))

    // FeeGroups are per-session documents — a hardcoded, build-time Tuition
    // FeeGroup id can only ever match one specific session, and silently
    // breaks (backend: "No fee types found for fee group") the moment a new
    // session starts. Use the id actually resolved for THIS student's
    // current session/class instead.
    const tuitionFeeGroupId =
      tuitionFeeTypes[0]?.feeGroup?._id || (TUITION_FEE_GROUP as string)

    const monthlyInvoices: InvoiceItem[] = monthlyFeesData.map(item => ({
      feeGroupId: tuitionFeeGroupId,
      // Explicit — bill exactly the fee type this row is for, nothing else
      // in the group. Without this the backend would bill every active fee
      // type in the group, including ones the staff deleted for this student.
      feeTypeIds: [item.feeTypeId].filter(Boolean),
      month: formatMonthToBackend(item.month),
      status: "paid",
      fine: item.fine,
      discount: {
        profile: item.discount.profile,
        manual: item.discount.manual,
      },
    }))

    const otherFeesInvoices: InvoiceItem[] = otherFeesData
      .filter(item => !isLibraryFineItem(item))
      .map(item => ({
        feeGroupId: item.feeGroupId || tuitionFeeGroupId,
        feeTypeId: item.feeTypeId,
        month: "other",
        status: "paid",
        fine: 0,
        discount: item.discount,
      }))

    const allInvoices = [...monthlyInvoices, ...otherFeesInvoices]

    setCreateFeeLoading(true)
    try {
      const requestData: MonthlyFeeRequest = {
        enrollmentId: studentData._id,
        invoices: allInvoices,
        ...(selectedLibraryFines.length > 0 && {
          libraryFines: selectedLibraryFines,
        }),
      }
      setWithPrint(!!isPrint)
      const response = await createMonthlyFee(requestData)

      if (response.success) {
        toast.success(response.message || "Fees created successfully!")

        if (isPrint) {
          setGeneratedInvoice({
            ...response.data,
            createdBy: {
              firstName: user.firstName,
              lastName: user.lastName,
            },
            studentSnapshot: {
              firstName: studentData?.user?.firstName,
              lastName: studentData?.user?.lastName,
              className: studentData?.class?.name,
              section: studentData?.section?.name,
              grNumber: studentData?.student?.grNumber,
            },
            monthlyFees: monthlyFeesData,
            otherFees: otherFeesData,
            grandTotal: calculateGrandTotal(),
          })
          setShowPrintModal(true)
        }

        handleCloseModal()
      } else {
        toast.error(response.message || "Failed to create monthly fee")
      }
    } catch (error: any) {
      console.error("Error creating monthly fee:", error)
      if (error.response?.data?.message) {
        toast.error(error.response.data.message)
      } else if (error.message) {
        toast.error(error.message)
      } else {
        toast.error("Failed to create monthly fee")
      }
    } finally {
      setCreateFeeLoading(false)
    }
  }

  const getFee = useCallback(async () => {
    if (!studentData?._id || !feeGroup) return

    setLoading(true)
    setFeeErrorMessage(null)
    setMonthlyFeesData([])
    setOtherFeesData([])
    setRemainingMonths([])
    setAllAvailableMonths([])
    setOtherFees([])

    try {
      const discountAndMonthRes = await getDiscountByEnrollment(studentData._id)

      const shortMonths =
        discountAndMonthRes.data.remainingMonths.map(formatMonthToShort)

      setAllAvailableMonths(shortMonths)
      setRemainingMonths(shortMonths)

      const paidFeeTypesByMonthLocal =
        discountAndMonthRes.data.paidFeeTypesByMonth || {}
      setPaidFeeTypesByMonth(paidFeeTypesByMonthLocal)

      const discountDataFromAPI = discountAndMonthRes.data
        .discount as DiscountData
      setDiscountData(discountDataFromAPI)

      const feeTypeRes = await getAllFeeType({
        classes: studentData?.class._id,
        system: selectedSystem,
        session: studentData?.session?._id,
        status: "active",
      })

      // Every active FeeType in the Tuition group — not just the first one —
      // so anything else placed there (e.g. STEAM Education Fee) bills
      // monthly alongside Tuition Fee instead of being silently dropped.
      // Tuition Fee always sorts first, everything else after in whatever
      // order the API returned it.
      const tuitionFeeTypesLocal = feeTypeRes.data.length
        ? feeTypeRes.data
            .filter((fee: any) => fee.feeGroup?.name === "Tuition")
            .sort((a: any, b: any) => {
              const aIsTuition = /tuition/i.test(a?.name || "") ? 0 : 1
              const bIsTuition = /tuition/i.test(b?.name || "") ? 0 : 1
              return aIsTuition - bIsTuition
            })
        : []

      setTuitionFeeTypes(tuitionFeeTypesLocal)

      // Every "other fee" the backend returns here is simply "configured for
      // this class and not yet paid" — for the ~3,300 bulk-imported existing
      // students, who have zero invoice history by design, that means EVERY
      // one-time fee type (Admission Fee, Re-Admission Fee, Transfer Fee...)
      // looks unpaid, even for students admitted years ago. Auto-filling all
      // of them into the table unconditionally would put a stray Rs 10,000
      // "Admission Fee" in front of staff on every single student. These are
      // click-to-add only now (via the "SELECT OTHER FEES" buttons below),
      // matching how tuition months are only added once someone picks them.
      //
      // `confirmedDue` is the one exception: it means a real, already-
      // existing UNPAID invoice sits against this fee type right now (e.g.
      // the Re-Admission Fee auto-generated the moment a student was struck
      // off) -- not a maybe. Staff forgetting to click it meant the fee
      // just silently never got collected, with no error and nothing in
      // the cart to show it was missed. These auto-add, same as an
      // already-due month, and can still be removed like any other row.
      const backendOtherFees = discountAndMonthRes.data.otherFees || []
      setOtherFees(backendOtherFees)

      const confirmedDueFees = backendOtherFees.filter((fee: any) => fee.confirmedDue)
      if (confirmedDueFees.length > 0) {
        setOtherFeesData(prev => {
          const existingIds = new Set(prev.map(item => item.feeTypeId))
          const newRows: OtherFeeItem[] = confirmedDueFees
            .filter((fee: any) => !existingIds.has(fee._id))
            .map((fee: any) => ({
              id: `other-${fee._id}`,
              name: fee.name,
              amount: fee.amount,
              discount: { profile: 0, manual: 0 },
              note: `${fee.feeGroupName} Fee -- already due`,
              feeGroupId: fee.feeGroup,
              feeGroupName: fee.feeGroupName,
              feeTypeId: fee._id,
            }))
          return newRows.length ? [...prev, ...newRows] : prev
        })
      }

      const tempCalculateDiscount = (baseFee: number) => {
        if (!discountDataFromAPI) return 0

        return discountDataFromAPI.discountType === "percentage"
          ? baseFee * (discountDataFromAPI.discount / 100)
          : discountDataFromAPI.discount
      }

      // Any month whose 1st has already passed belongs in the table
      // automatically, ready for collection — not just months old enough
      // to already owe a fine (that only starts on day 11).
      const monthsToAutoAdd = shortMonths.filter(isMonthStarted)

      if (monthsToAutoAdd.length > 0) {
        // One row per active FeeType in the group that isn't already
        // invoiced for that month — same as a manual month click, just
        // auto-populated for months already due.
        const autoAddedFees: MonthlyFeeItem[] = monthsToAutoAdd.flatMap(month => {
          const alreadyPaidIds =
            paidFeeTypesByMonthLocal[formatMonthToBackend(month)] || []
          return tuitionFeeTypesLocal
            .filter((ft: any) => !alreadyPaidIds.includes(ft._id))
            .map((ft: any) =>
              buildFeeRow(month, ft, tempCalculateDiscount),
            )
        })

        setMonthlyFeesData(autoAddedFees)
        setRemainingMonths(prev =>
          prev.filter(month => !monthsToAutoAdd.includes(month)),
        )
      }
    } catch (error: any) {
      console.error("Error in getFee:", error)
      if (error.response?.data?.message) {
        setFeeErrorMessage(error.response.data.message)
      } else if (error.message) {
        setFeeErrorMessage(error.message)
      } else {
        setFeeErrorMessage("Failed to load fee data")
      }
    } finally {
      setLoading(false)
    }
  }, [studentData?._id, feeGroup, selectedSystem])

  useEffect(() => {
    if (studentData?._id && feeGroup) {
      getFee()
    }
  }, [studentData?._id, feeGroup])

  useEffect(() => {
    if (allAvailableMonths.length > 0) {
      const availableMonths = allAvailableMonths.filter(
        month => !monthlyFeesData.some(item => item.month === month),
      )
      setRemainingMonths(availableMonths)
    }
  }, [monthlyFeesData, allAvailableMonths])

  const handleCloseModal = () => {
    const el = document.getElementById("create_fee_modal")
    if (el) {
      el.setAttribute("data-bs-dismiss", "modal")
      el.click()
      el.removeAttribute("data-bs-dismiss")
    }
    setMonthlyFeesData([])
    setOtherFeesData([])
    setRemainingMonths([])
    setAllAvailableMonths([])
    setOtherFees([])
    setTuitionFeeTypes([])
    setDiscountData(undefined)
    setFeeErrorMessage(null)
    onClose()
  }

  const handleCloseButtonClick = () => {
    onClose()
    setMonthlyFeesData([])
    setOtherFeesData([])
    setRemainingMonths([])
    setAllAvailableMonths([])
    setOtherFees([])
    setTuitionFeeTypes([])
    setDiscountData(undefined)
    setFeeErrorMessage(null)
  }

  const handlePrintModalClose = () => {
    setShowPrintModal(false)
    setGeneratedInvoice(null)
    // handleCloseModal()
  }

  const availableOtherFees = otherFees
    .filter(
      (fee: any) =>
        !otherFeesData.some(selectedFee => selectedFee.feeTypeId === fee._id),
    )
    .map((fee: any) => ({
      id: `other-${fee._id}`,
      name: fee.name,
      amount: fee.amount,
      discount: {
        profile: 0,
        manual: 0,
      },
      note: `${fee.feeGroupName} Fee${
        fee.applicableFrom
          ? ` - ${new Date(fee.applicableFrom).toLocaleDateString()}`
          : ""
      }`,
      feeGroupId: fee.feeGroup,
      feeGroupName: fee.feeGroupName,
      feeTypeId: fee._id,
      metaData: fee.metaData,
    }))

  return (
    <>
      <div className={"modal fade"} id="create_fee_modal">
        <div className="modal-dialog modal-dialog-centered modal-xl">
          <div className="modal-content">
            <div className="modal-header">
              <h4 className="modal-title">ALL FEES</h4>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                onClick={handleCloseButtonClick}
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <form>
              <div
                className="modal-body"
                style={{ maxHeight: "70vh", overflowY: "auto" }}
              >
                {studentData && (
                  <div className="bg-light p-3 pb-0 rounded mb-4">
                    <div className="row align-items-center">
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">Name</span>
                          <p className="text-dark mb-0">
                            {studentData?.user?.firstName || "N/A"}{" "}
                            {studentData?.user?.lastName}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">
                            GR Number
                          </span>
                          <p className="text-dark mb-0">
                            {studentData?.student?.grNumber || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">Grade</span>
                          <p className="text-dark mb-0">
                            {studentData?.class?.name || "N/A"}
                          </p>
                        </div>
                      </div>
                      <div className="col-lg-3 col-md-6">
                        <div className="mb-3">
                          <span className="fs-12 mb-1 text-muted">Section</span>
                          <p className="text-dark mb-0">
                            {studentData?.section?.name || "N/A"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="row mb-4">
                  <div className="col-12">
                    <div className="mb-3">
                      <h6 className="text-muted mb-1">Previous Session</h6>
                      <p className="mb-0 small text-muted">
                        Please check old system for previous month fees
                      </p>
                    </div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-1" />
                      ) : (
                        <h6 className="text-muted mb-0">
                          This Session (Available: {remainingMonths.length} of{" "}
                          {allAvailableMonths.length})
                        </h6>
                      )}
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className={`btn btn-primary btn-sm ${
                            user.permissions?.includes("inoices.view") ||
                            !user.permissions?.length
                              ? ""
                              : "not-allowed"
                          }`}
                          onClick={e => {
                            if (
                              !user.permissions?.includes("fees.view") &&
                              !!user.permissions?.length
                            ) {
                              e.preventDefault()
                            } else {
                              handleViewInvoices()
                            }
                          }}
                        >
                          View Invoices
                          {!user.permissions?.includes("fees.view") &&
                            !!user.permissions?.length && (
                              <small className="ms-1">(No permission)</small>
                            )}
                        </button>
                      </div>
                    </div>
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      {loading ? (
                        <div className="text-muted">Loading months...</div>
                      ) : feeErrorMessage ? (
                        <div className="text-muted">{feeErrorMessage}</div>
                      ) : remainingMonths.length > 0 ? (
                        remainingMonths.map(month => {
                          const fineAmount = calculateFine(month)
                          const hasFine = fineAmount > 0

                          return (
                            <button
                              key={month}
                              type="button"
                              className={`btn btn-sm ${
                                hasFine ? "btn-warning" : "btn-outline-primary"
                              }`}
                              onClick={() => handleMonthSelect(month)}
                              title={`Click to add ${month} to fees table${
                                hasFine ? ` (Fine: ${fineAmount})` : ""
                              }`}
                            >
                              {month}
                            </button>
                          )
                        })
                      ) : (
                        <div className="text-muted">
                          {monthlyFeesData.length > 0
                            ? "All available months have been added"
                            : "No remaining months available for this student"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    <h5 className="mb-3 text-primary">MONTHLY FEES</h5>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <table className="table table-bordered">
                        <thead
                          className="table-light"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          <tr>
                            <th style={{ width: "120px" }}>MONTH</th>
                            <th style={{ width: "170px" }}>FEE TYPE</th>
                            <th style={{ width: "110px" }}>FEES</th>
                            <th style={{ width: "100px" }}>MANUAL DISCOUNT</th>
                            <th style={{ width: "130px" }}>PROFILE DISCOUNT</th>
                            <th style={{ width: "100px" }}>FINE</th>
                            <th style={{ width: "170px" }}>AMOUNT</th>
                            <th style={{ width: "260px" }}>NOTE</th>
                            <th style={{ width: "80px" }}>ACTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyFeesData.map((feeItem, index) => {
                            // Read the discount already stored on this row
                            // (correctly zero for every fee type except
                            // Tuition Fee — see buildFeeRow) instead of
                            // recomputing it here, which ignored that and
                            // showed the full discount on every row.
                            const profileDiscountAmount = feeItem.discount.profile
                            const maxAllowedDiscount = calculateMaxAllowedDiscount(
                              feeItem.fees,
                              feeItem.fine,
                              profileDiscountAmount,
                            )
                            const totalAmount = Math.max(
                              0,
                              feeItem.fees -
                                feeItem.discount.manual -
                                profileDiscountAmount +
                                feeItem.fine,
                            )
                            const hasFine = feeItem.fine > 0

                            return (
                              <tr
                                key={feeItem.id}
                                className={hasFine ? "table-warning" : ""}
                              >
                                <td>
                                  <strong>{feeItem.month}</strong>
                                  {hasFine && (
                                    <span className="badge bg-warning ms-2">
                                      Fine
                                    </span>
                                  )}
                                </td>

                                {/* FEE TYPE — its own row per fee, same as
                                    Tuition Fee always had; delete the row for
                                    any student who shouldn't be billed it. */}
                                <td>{feeItem.feeTypeName}</td>

                                {/* FEES */}
                                <td>
                                  <input
                                    type="text"
                                    className="form-control bg-light fee-input-large"
                                    value={feeItem.fees || ""}
                                    readOnly
                                    title="Fees amount"
                                  />
                                </td>

                                {/* MANUAL DISCOUNT */}
                                <td>
                                  <div className="position-relative">
                                    <input
                                      type="number"
                                      className={`form-control ${
                                        feeItem.discount.manual >
                                        maxAllowedDiscount
                                          ? "is-invalid"
                                          : ""
                                      } ${
                                        user.permissions?.includes(
                                          "fees.edit",
                                        ) || !user.permissions?.length
                                          ? ""
                                          : "bg-light not-allowed"
                                      }`}
                                      readOnly={
                                        !(
                                          user.permissions?.includes(
                                            "fees.edit",
                                          ) || !user.permissions?.length
                                        )
                                      }
                                      value={feeItem.discount.manual || ""}
                                      onChange={e =>
                                        handleFeeChange(
                                          index,
                                          "discount.manual",
                                          e.target.value,
                                        )
                                      }
                                      max={maxAllowedDiscount}
                                      title={`Maximum discount allowed: ${maxAllowedDiscount}`}
                                    />
                                  </div>
                                </td>

                                {/* PROFILE DISCOUNT */}
                                <td>
                                  <div className="input-group">
                                    <input
                                      type="text"
                                      className="form-control bg-light"
                                      value={profileDiscountAmount || 0}
                                      readOnly
                                    />
                                    <span className="input-group-text">
                                      {discountData?.discountType ===
                                      "percentage"
                                        ? "%"
                                        : "PKR"}
                                    </span>
                                  </div>
                                </td>

                                {/* FINE */}
                                <td>
                                  <input
                                    type="number"
                                    className={`form-control fine-input ${
                                      hasFine ? "bg-warning bg-opacity-25" : ""
                                    }`}
                                    value={feeItem.fine || ""}
                                    onChange={e =>
                                      handleFeeChange(
                                        index,
                                        "fine",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>

                                {/* AMOUNT */}
                                <td>
                                  <input
                                    type="text"
                                    className="form-control bg-light"
                                    style={{ minWidth: "80px" }}
                                    value={totalAmount}
                                    readOnly
                                  />
                                </td>

                                {/* NOTE */}
                                <td>
                                  <input
                                    type="text"
                                    className={`form-control ${
                                      user.permissions?.includes("fees.edit") ||
                                      !user.permissions?.length
                                        ? ""
                                        : "bg-light not-allowed"
                                    }`}
                                    style={{ minWidth: "80px" }}
                                    readOnly={
                                      !(
                                        user.permissions?.includes(
                                          "fees.edit",
                                        ) || !user.permissions?.length
                                      )
                                    }
                                    value={feeItem.note}
                                    onChange={e =>
                                      handleFeeChange(
                                        index,
                                        "note",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>

                                {/* ACTION */}
                                <td>
                                  <button
                                    type="button"
                                    className={`btn btn-danger btn-sm ${
                                      user.permissions?.includes("fees.edit") ||
                                      !user.permissions?.length
                                        ? ""
                                        : "not-allowed"
                                    }`}
                                    onClick={e => {
                                      if (
                                        user.permissions?.includes(
                                          "fees.edit",
                                        ) ||
                                        !user.permissions?.length
                                      ) {
                                        removeFeeRow(index)
                                      } else {
                                        e.preventDefault()
                                      }
                                    }}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            )
                          })}

                          {monthlyFeesData.length === 0 && (
                            <tr>
                              <td
                                colSpan={9}
                                className="text-center text-muted py-4"
                              >
                                No months added yet. Click on available months
                                above to add them.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row mb-4">
                  <div className="col-12">
                    {availableOtherFees.length > 0 && (
                      <div className="mb-3">
                        <h6 className="text-muted mb-4">
                          SELECT OTHER FEES (Available:{" "}
                          {availableOtherFees.length})
                        </h6>
                        <div className="d-flex flex-wrap gap-2 mb-3">
                          {availableOtherFees.map(feeItem => (
                            <button
                              key={feeItem.id}
                              type="button"
                              className="btn btn-sm btn-outline-primary mb-4"
                              onClick={() => handleOtherFeeSelect(feeItem)}
                              title={`Click to add ${feeItem.name} to fees table`}
                            >
                              {feeItem.name} - {feeItem.amount}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h5 className="text-primary mb-0">OTHER FEES</h5>
                    </div>
                    <div
                      className="table-responsive"
                      style={{ maxHeight: "400px", overflowY: "auto" }}
                    >
                      <table className="table table-bordered">
                        <thead
                          className="table-light"
                          style={{ position: "sticky", top: 0, zIndex: 1 }}
                        >
                          <tr>
                            <th>FEE NAME</th>
                            <th>TYPE</th>
                            <th>FEES</th>
                            <th>MANUAL DISCOUNT</th>
                            <th>AMOUNT</th>
                            <th>NOTE</th>
                            <th>ACTION</th>
                          </tr>
                        </thead>
                        <tbody>
                          {otherFeesData.map((feeItem, index) => {
                            const totalAmount =
                              feeItem.amount - feeItem.discount.manual

                            const canEdit =
                              user.permissions?.includes("fees.edit") ||
                              !user.permissions?.length

                            return (
                              <tr key={feeItem.id}>
                                {/* FEE NAME */}
                                <td>
                                  <strong>{feeItem.name}</strong>
                                </td>

                                {/* TYPE */}
                                <td>
                                  <span className="badge bg-info">
                                    {feeItem.feeGroupName || "Other"}
                                  </span>
                                </td>

                                {/* FEES */}
                                <td>
                                  <input
                                    type="text"
                                    className="form-control bg-light"
                                    value={feeItem.amount || ""}
                                    readOnly
                                    title="Fees amount from API"
                                  />
                                </td>

                                {/* MANUAL DISCOUNT */}
                                <td>
                                  <input
                                    type="number"
                                    className={`form-control ${
                                      feeItem.discount.manual > feeItem.amount
                                        ? "is-invalid"
                                        : ""
                                    } ${canEdit ? "" : "bg-light not-allowed"}`}
                                    value={feeItem.discount.manual || ""}
                                    readOnly={!canEdit}
                                    onChange={e =>
                                      handleOtherFeeChange(
                                        index,
                                        "discount.manual",
                                        e.target.value,
                                      )
                                    }
                                    max={feeItem.amount}
                                    title={`Maximum discount allowed: ${feeItem.amount}`}
                                  />
                                </td>

                                {/* AMOUNT */}
                                <td>
                                  <input
                                    type="text"
                                    className="form-control bg-light"
                                    value={totalAmount}
                                    readOnly
                                  />
                                </td>

                                {/* NOTE */}
                                <td>
                                  <input
                                    type="text"
                                    className={`form-control ${
                                      canEdit ? "" : "bg-light not-allowed"
                                    }`}
                                    readOnly={!canEdit}
                                    value={feeItem.note}
                                    onChange={e =>
                                      handleOtherFeeChange(
                                        index,
                                        "note",
                                        e.target.value,
                                      )
                                    }
                                  />
                                </td>

                                {/* ACTION */}
                                <td>
                                  <button
                                    type="button"
                                    className={`btn btn-danger btn-sm ${
                                      canEdit ? "" : "not-allowed"
                                    }`}
                                    onClick={e => {
                                      if (canEdit) {
                                        removeOtherFeeRow(index)
                                      } else {
                                        e.preventDefault()
                                      }
                                    }}
                                  >
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            )
                          })}

                          {otherFeesData.length === 0 && (
                            <tr>
                              <td
                                colSpan={7}
                                className="text-center text-muted py-4"
                              >
                                No other fees added yet. Select from available
                                fees above.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <div className="p-3 rounded text-end bg-light">
                      <h4 className="mb-0 text-primary">
                        GRAND TOTAL: {calculateGrandTotal().toLocaleString()}
                      </h4>
                      <p className="text-muted mb-0">
                        Includes {monthlyFeesData.length} month(s) tuition fees
                        and {otherFeesData.length} other fee(s)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="modal-footer pt-3">
                <button
                  type="button"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                {/* 
                <button
                  type="button"
                  className={`btn btn-primary me-2 ${
                    user.permissions?.includes("fees.collect") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  onClick={() => handleGenerateInvoice()}
                  disabled={
                    (monthlyFeesData.length === 0 &&
                      otherFeesData.length === 0) ||
                    createFeeLoading ||
                    !studentData?._id ||
                    !(
                      user.permissions?.includes("fees.collect") ||
                      !user.permissions?.length
                    )
                  }
                >
                  {!withPrint && createFeeLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Processing...
                    </>
                  ) : (
                    "Pay Fee"
                  )}
                </button> */}

                <button
                  type="button"
                  className={`btn btn-primary ${
                    user.permissions?.includes("fees.collect") ||
                    !user.permissions?.length
                      ? ""
                      : "not-allowed"
                  }`}
                  onClick={() => handleGenerateInvoice(true)}
                  disabled={
                    (monthlyFeesData.length === 0 &&
                      otherFeesData.length === 0) ||
                    createFeeLoading ||
                    !studentData?._id ||
                    !(
                      user.permissions?.includes("fees.collect") ||
                      !user.permissions?.length
                    )
                  }
                >
                  {withPrint && createFeeLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Processing...
                    </>
                  ) : (
                    "Generate & Pay Invoice"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <InvoicePrintModal
        show={showPrintModal}
        onClose={handlePrintModalClose}
        onPrintWithOption={handlePrint}
        invoice={generatedInvoice}
        studentDetails={{
          firstName: studentData?.user.firstName,
          lastName: studentData?.user.lastName,
          className: studentData?.class.name,
          section: studentData?.section.name,
          grNumber: studentData?.student.grNumber,
        }}
        branch={studentData?.branch}
        printRef={printRef}
        calculateFinalAmount={calculateFinalAmount}
      />
    </>
  )
}

export default CollectFeesModal
